import { useState } from "react";
import { api, fmtCurrency } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain } from "lucide-react";

const SCENARIOS = [
  { v: "expense", l: "Kann ich mir diese Ausgabe leisten?", amountLabel: "Betrag (€)" },
  { v: "hire", l: "Kann ich einen Mitarbeiter einstellen?", amountLabel: "Monatsgehalt brutto (€)" },
  { v: "owner_draw", l: "Kann ich mir X € entnehmen?", amountLabel: "Entnahme (€)" },
  { v: "tool_purchase", l: "Kann ich ein neues Tool kaufen?", amountLabel: "Monatliche Toolkosten (€)" },
  { v: "low_revenue_month", l: "Was, wenn ich einen Monat mit weniger Umsatz habe?", amountLabel: "Umsatzausfall (€)" },
  { v: "late_payment", l: "Was, wenn Kunde X 30 Tage später zahlt?", amountLabel: "Verspätete Zahlung (€)" },
];

export default function DecisionsPage() {
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState(5000);
  const [duration, setDuration] = useState(6);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const simulate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/decisions/simulate", {
        scenario_type: type,
        amount: Number(amount),
        duration_months: Number(duration),
      });
      setResult(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const cur = SCENARIOS.find(s => s.v === type);
  const showDuration = ["hire", "tool_purchase", "late_payment"].includes(type);

  return (
    <div className="space-y-6" data-testid="decisions-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Entscheidungsmodus</div>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-3">
          <Brain className="h-7 w-7" /> Kann ich mir das leisten?
        </h1>
        <p className="text-sm text-neutral-500 mt-2">Spiele Was-wäre-wenn-Szenarien durch. AgencyCash berechnet die Auswirkung auf freien Cashflow, Fixkostendeckung und Liquiditätsreichweite.</p>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="sm:col-span-3">
            <Label>Was möchtest du wissen?</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="decision-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SCENARIOS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>{cur.amountLabel}</Label>
            <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="decision-amount" />
          </div>
          {showDuration && (
            <div>
              <Label>{type === "late_payment" ? "Verzug (Monate)" : "Dauer (Monate)"}</Label>
              <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="decision-duration" />
            </div>
          )}
          <div className="flex items-end">
            <Button onClick={simulate} disabled={loading} className="w-full" data-testid="decision-simulate">{loading ? "…" : "Simulieren"}</Button>
          </div>
        </div>
      </div>

      {result && (
        <div className="rounded-xl border-2 p-6" style={{ borderColor: result.traffic_light === "red" ? "#FCA5A5" : result.traffic_light === "yellow" ? "#FCD34D" : "#86EFAC", backgroundColor: result.traffic_light === "red" ? "#FEF2F2" : result.traffic_light === "yellow" ? "#FFFBEB" : "#F0FDF4" }} data-testid="decision-result">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-2">Empfehlung</div>
          <div className="font-display text-2xl font-semibold mb-4" data-testid="decision-recommendation">{result.recommendation}</div>
          <p className="text-sm text-neutral-700 mb-6">{result.description} — Auswirkung gesamt: <strong>{fmtCurrency(result.impact_total)}</strong>{result.additional_monthly_cost > 0 ? `, davon ${fmtCurrency(result.additional_monthly_cost)} zusätzlich pro Monat` : ""}.</p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <BeforeAfter label="Kontostand" before={result.balance_before} after={result.balance_after} />
            <BeforeAfter label="Frei verfügbar" before={result.free_money_before} after={result.free_money_after} />
            <BeforeAfter label="Monatliche Fixkosten" before={result.monthly_fixed_before} after={result.monthly_fixed_after} reverse />
            <BeforeAfter label="Liquiditätsreichweite" before={result.runway_before_months} after={result.runway_after_months} suffix=" M" isMoney={false} />
          </div>
        </div>
      )}
    </div>
  );
}

function BeforeAfter({ label, before, after, reverse = false, suffix = "", isMoney = true }) {
  const delta = (after || 0) - (before || 0);
  const goodWhenPositive = !reverse;
  const isGood = goodWhenPositive ? delta >= 0 : delta <= 0;
  return (
    <div className="rounded-lg bg-white border border-neutral-200 p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="flex items-baseline gap-2 mt-2">
        <span className="text-neutral-500 text-sm">{isMoney ? fmtCurrency(before) : `${before ?? "—"}${suffix}`}</span>
        <span className="text-neutral-400">→</span>
        <span className={`font-display text-lg font-semibold ${isGood ? "text-emerald-700" : "text-red-600"}`}>{isMoney ? fmtCurrency(after) : `${after ?? "—"}${suffix}`}</span>
      </div>
    </div>
  );
}
